import { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '@municipality/firebase';

export interface MunicipalityStatistics {
  news: number;
  events: number;
  citizens: number;
  issues: number;
  surveys: number;
  lostAndFound: number;
  poi: number;
}

export const useMunicipalityStatistics = (municipalityId: string | null) => {
  const [statistics, setStatistics] = useState<MunicipalityStatistics>({
    news: 0,
    events: 0,
    citizens: 0,
    issues: 0,
    surveys: 0,
    lostAndFound: 0,
    poi: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      return;
    }

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        // Helper function to safely get count for subcollections
        const safeGetSubcollectionCount = async (
          subcollectionName: string,
          name: string
        ): Promise<number> => {
          try {
            const snapshot = await getDocs(
              query(collection(db, 'municipalities', municipalityId, subcollectionName))
            );
            return snapshot.size;
          } catch (err) {
            console.warn(`Failed to fetch ${name} count:`, err);
            return 0;
          }
        };

        // Fetch all counts in parallel
        const [
          newsCount,
          eventsCount,
          citizensCount,
          issuesCount,
          surveysCount,
          lostAndFoundCount,
          poiCount,
        ] = await Promise.all([
          // News count
          safeGetSubcollectionCount('news', 'News'),
          // Events count
          safeGetSubcollectionCount('events', 'Events'),
          // Citizens count - count unique citizens who have interacted with this municipality
          // (through issues, lost and found, surveys, etc.)
          (async () => {
            try {
              const uniqueCitizenIds = new Set<string>();
              
              // Count citizens from issues
              try {
                const issuesSnapshot = await getDocs(
                  query(collection(db, 'municipalities', municipalityId, 'issues'))
                );
                issuesSnapshot.forEach((doc) => {
                  const issueData = doc.data();
                  if (
                    issueData.reportedBy &&
                    issueData.reportedBy.role === 'citizen' &&
                    issueData.reportedBy.userId
                  ) {
                    uniqueCitizenIds.add(issueData.reportedBy.userId);
                  }
                });
              } catch (err) {
                console.warn('Failed to fetch citizens from issues:', err);
              }
              
              // Count citizens from lost and found
              try {
                const lostAndFoundSnapshot = await getDocs(
                  query(collection(db, 'municipalities', municipalityId, 'lostAndFound'))
                );
                lostAndFoundSnapshot.forEach((doc) => {
                  const recordData = doc.data();
                  if (
                    recordData.submittedBy &&
                    recordData.submittedBy.role === 'citizen' &&
                    recordData.submittedBy.userId
                  ) {
                    uniqueCitizenIds.add(recordData.submittedBy.userId);
                  }
                });
              } catch (err) {
                console.warn('Failed to fetch citizens from lost and found:', err);
              }
              
              return uniqueCitizenIds.size;
            } catch (err) {
              console.warn('Failed to fetch Citizens count:', err);
              return 0;
            }
          })(),
          // Issues count
          safeGetSubcollectionCount('issues', 'Issues'),
          // Surveys count
          safeGetSubcollectionCount('surveys', 'Surveys'),
          // Lost and Found count
          safeGetSubcollectionCount('lostAndFound', 'Lost and Found'),
          // POI count
          safeGetSubcollectionCount('poi', 'POI'),
        ]);

        setStatistics({
          news: newsCount,
          events: eventsCount,
          citizens: citizensCount,
          issues: issuesCount,
          surveys: surveysCount,
          lostAndFound: lostAndFoundCount,
          poi: poiCount,
        });
      } catch (err) {
        console.error('Error fetching municipality statistics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [municipalityId]);

  return { statistics, loading, error };
};
